import { useRouter } from "next/router";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Spinner } from "@/components/shared/spinner";
import {
  fetchSpeakers,
  getAllSpeakers,
  getIsSpeakersLoading,
} from "@/features/speakers/speakers-reducer";
import {
  fetchSponsors,
  getAllSponsors,
  getIsSponsorsLoading,
} from "@/features/sponsors/sponsors-reducer";
import {
  fetchVendors,
  getAllVendors,
  getIsVendorsLoading,
} from "@/features/vendors/vendors-reducer";
import endpoints from "@/utils/endpoints";

import DataExistsCard from "./data-exists-card-component";
import EmptyStateCard from "./empty-state-card-component";
import ImportList, { ImportableTypes } from "./import-list-component";

export type ImportBaseProps = {
  title: string;
  type: "speakers" | "sponsors" | "vendors";
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  importText: string;
  importButtonText: string;
  onImportClick: (args: ImportableTypes[]) => void;
  mainEmptyText: string;
  subEmptyText: string;
  informationText?: string;
};

const get = endpoints();

export default function ImportBase(props: ImportBaseProps) {
  const { query } = useRouter();
  const slug = query.slug as string;

  const dispatch = useDispatch();

  const fetchData = React.useCallback(() => {
    switch (props.type) {
      case "speakers": {
        return dispatch(fetchSpeakers(slug));
      }
      case "sponsors": {
        return dispatch(fetchSponsors(slug));
      }
      case "vendors": {
        return dispatch(fetchVendors(slug));
      }
    }
  }, [props.type, dispatch, slug]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  let loadingSelector;
  let dataSelector;

  if (props.type === "speakers") {
    loadingSelector = getIsSpeakersLoading;
    dataSelector = getAllSpeakers;
  }
  if (props.type === "sponsors") {
    loadingSelector = getIsSponsorsLoading;
    dataSelector = getAllSponsors;
  }
  if (props.type === "vendors") {
    loadingSelector = getIsVendorsLoading;
    dataSelector = getAllVendors;
  }

  const loading = useSelector(loadingSelector);
  const dataList = useSelector(dataSelector) as [];

  const [isShowingList, setShowingList] = React.useState(false);
  const handleShowList = React.useCallback(() => {
    fetchData();
    setShowingList(true);
  }, [fetchData]);

  const generateHref = React.useMemo(() => {
    return get("HOPIN_DASHBOARD_PATH")(slug, props.type);
  }, [slug, props.type]);

  if (loading) {
    return (
      <div className="flex justify-center">
        <Spinner dataTestId="data-loading" size="small" />
      </div>
    );
  }

  if (dataList.length === 0) {
    return (
      <EmptyStateCard
        icon={props.icon}
        mainEmptyText={props.mainEmptyText}
        subEmptyText={props.subEmptyText}
        href={generateHref}
        buttonText={props.importButtonText}
        onImportClick={handleShowList}
      />
    );
  }

  return (
    <div data-testid="speakers-settings">
      {isShowingList ? (
        <ImportList
          title={props.title}
          onAddSelected={props.onImportClick}
          list={dataList}
          type={props.type}
          informationText={props.informationText}
        />
      ) : (
        <DataExistsCard
          href={generateHref}
          icon={props.icon}
          importText={props.importText}
          buttonText={props.importButtonText}
          onClickImport={handleShowList}
        />
      )}
    </div>
  );
}
